/**
 * Dashboard data hooks
 */

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { useFirm } from '../../../hooks/use-firm'
import { queryKeys } from '../../../lib/query-keys'
import type { DashboardMetrics, RiskScore, Notification } from '../types/dashboard.types'

export function useDashboardMetrics() {
  const { firmId } = useFirm()

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.dashboard.metrics(firmId!),
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('dashboard_metrics')
        .select('*')
        .eq('firm_id', firmId)
        .single()

      if (error) throw error
      return data as DashboardMetrics
    },
    enabled: !!firmId,
    staleTime: 5 * 60 * 1000,  // 5 min for dashboard
  })

  return { metrics: data ?? null, loading: isLoading, error: error as Error | null }
}

export function useRiskScores(entityType?: 'client' | 'engagement' | 'task') {
  const { firmId } = useFirm()

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.dashboard.riskScores(firmId!, entityType),
    queryFn: async () => {
      let query = (supabase as any)
        .from('risk_scores')
        .select('*')
        .eq('firm_id', firmId)
        .order('score', { ascending: false })

      if (entityType) {
        query = query.eq('entity_type', entityType)
      }

      const { data, error } = await query

      if (error) throw error
      return (data || []) as RiskScore[]
    },
    enabled: !!firmId,
  })

  return { riskScores: data ?? [], loading: isLoading, error: error as Error | null }
}

export function useNotifications() {
  const { firmId } = useFirm()

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.dashboard.notifications(firmId!),
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('notifications')
        .select('*')
        .eq('firm_id', firmId)
        .eq('is_read', false)
        .order('created_at', { ascending: false })
        .limit(10)

      if (error) throw error
      return (data || []) as Notification[]
    },
    enabled: !!firmId,
  })

  return { notifications: data ?? [], loading: isLoading, error: error as Error | null }
}

export function useMarkNotificationRead() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const { firmId } = useFirm()
  const queryClient = useQueryClient()

  const markAsRead = async (notificationId: string): Promise<void> => {
    try {
      setLoading(true)
      const { error: updateError } = await (supabase as any)
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId)

      if (updateError) throw updateError
      setError(null)

      // Invalidate notifications query
      queryClient.invalidateQueries({ queryKey: ['dashboard', firmId, 'notifications'] })
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to mark notification as read')
      setError(error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  return { markAsRead, loading, error }
}
